import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, ChevronLeft, ChevronRight, Trash2, FolderKanban, Users, Activity, FolderOpen, CheckCircle, Ban, AlertTriangle, X, ArrowUpRightFromCircle } from "lucide-react";
import DeleteConfirmModal from "../../components/DeleteConfirmModal";
import { listProjects, deleteProject, updateProjectStatus } from "../../services/projectService";
import { formatPid } from "../../utils/formatPid";
import "../../assets/css/ProjectList.css";

const AVATAR_COLORS = [
  { bg: "var(--primaryTint)", color: "var(--primaryColor)" },
  { bg: "#f0fdf4", color: "#166534" },
  { bg: "#fdf4ff", color: "#7e22ce" },
  { bg: "#fff7ed", color: "#c2410c" },
  { bg: "#ede9fe", color: "#6d28d9" },
];

const ProjectList = () => {
  const itemsPerPage = 10;
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [showSuspendModal, setShowSuspendModal] = useState(false);
  const [pendingSuspendPid, setPendingSuspendPid] = useState(null);
  const [projectList, setProjectList] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [projectStatuses, setProjectStatuses] = useState({});
  const [stats, setStats] = useState({ totalProjects: 0, activeProjects: 0, totalManagers: 0, noManagers: 0 });

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  useEffect(() => {
    const fetchProjects = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await listProjects(currentPage, itemsPerPage, debouncedSearch);
        const response = res.data?.response ?? {};
        const list = Array.isArray(response.items) ? response.items : [];
        setProjectList(list);
        setTotalCount(response.pagination?.total ?? list.length);
        setProjectStatuses(Object.fromEntries(list.map((p) => [p.project_pid, p.is_active === 1 ? "active" : "inactive"])));
        const s = response.stats ?? {};
        setStats({
          totalProjects:  s.total_projects  ?? 0,
          activeProjects: s.active_projects ?? 0,
          totalManagers:  s.total_managers  ?? 0,
          noManagers:     s.no_managers     ?? 0,
        });
      } catch {
        setError("Failed to load projects.");
      } finally {
        setLoading(false);
      }
    };
    fetchProjects();
  }, [currentPage, debouncedSearch]);

  const toggleStatus = async (e, pid) => {
    e.stopPropagation();
    const current = projectStatuses[pid] ?? "active";
    if (current === "active") {
      setPendingSuspendPid(pid);
      setShowSuspendModal(true);
      return;
    }
    // activating — no confirmation needed
    setProjectStatuses((prev) => ({ ...prev, [pid]: "active" }));
    try {
      await updateProjectStatus(pid, true);
    } catch {
      setProjectStatuses((prev) => ({ ...prev, [pid]: current }));
    }
  };

  const confirmSuspend = async () => {
    const pid = pendingSuspendPid;
    setShowSuspendModal(false);
    setPendingSuspendPid(null);
    setProjectStatuses((prev) => ({ ...prev, [pid]: "suspended" }));
    try {
      await updateProjectStatus(pid, false);
    } catch {
      setProjectStatuses((prev) => ({ ...prev, [pid]: "active" }));
    }
  };

  const handleDeleteConfirm = async () => {
    if (!selectedProject) return;
    try {
      await deleteProject(selectedProject.project_pid);
      setProjectList((prev) => prev.filter((p) => p.project_pid !== selectedProject.project_pid));
      setTotalCount((c) => c - 1);
    } catch {
      // deletion failed — keep list as-is
    } finally {
      setShowDeleteModal(false);
      setSelectedProject(null);
    }
  };

  const getPaginationItems = (currentPage, totalPages) => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, index) => index + 1);
    }

    if (currentPage <= 4) {
      return [1, 2, 3, 4, 5, "end-ellipsis", totalPages];
    }

    if (currentPage >= totalPages - 3) {
      return [
        1,
        "start-ellipsis",
        totalPages - 4,
        totalPages - 3,
        totalPages - 2,
        totalPages - 1,
        totalPages,
      ];
    }

    return [
      1,
      "start-ellipsis",
      currentPage - 1,
      currentPage,
      currentPage + 1,
      "end-ellipsis",
      totalPages,
    ];
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / itemsPerPage));
  const pageStartIndex = (currentPage - 1) * itemsPerPage;
  const paginatedProjects = projectList;
  const paginationItems = getPaginationItems(currentPage, totalPages);

  useEffect(() => { setCurrentPage(1); }, [debouncedSearch]);


  const StatCard = ({ icon: Icon, iconBg, iconColor, label, value, sub }) => (
    <div style={{
      background: "#fff", border: "1px solid var(--borderColor)", borderRadius: 12,
      padding: "18px 20px", display: "flex", alignItems: "center", gap: 16,
      boxShadow: "0 1px 3px rgba(15,23,42,.05), 0 4px 12px rgba(15,23,42,.04)", width: "100%",
    }}>
      <div style={{
        width: 48, height: 48, borderRadius: 12, background: iconBg, color: iconColor,
        display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
      }}>
        <Icon size={22} />
      </div>
      <div style={{ minWidth: 0 }}>
        <p style={{ margin: 0, fontSize: 12, color: "var(--muteColor)", fontWeight: 500, marginBottom: 2 }}>{label}</p>
        <h5 style={{ margin: 0, fontSize: 24, fontWeight: 800, color: "#0f172a", lineHeight: 1.2 }}>{value}</h5>
        {sub && <p style={{ margin: "4px 0 0", fontSize: 11, color: "var(--muteColor)" }}>{sub}</p>}
      </div>
    </div>
  );

  return (
    <main className="app-wrapper url-list-page">
      <div className="container-fluid">

        {/* ── Stat cards ── */}
        <div className="row g-3 mb-4">
          <div className="col-12 col-sm-6 col-xl-3">
            <StatCard icon={FolderKanban} iconBg="var(--primaryTint)" iconColor="var(--primaryColor)"   label="Total Projects"  value={stats.totalProjects}  sub="All registered" />
          </div>
          <div className="col-12 col-sm-6 col-xl-3">
            <StatCard icon={Activity}     iconBg="#f0fdf4" iconColor="var(--successColor)"   label="Active Projects"  value={stats.activeProjects}  sub="Currently running" />
          </div>
          <div className="col-12 col-sm-6 col-xl-3">
            <StatCard icon={Users}        iconBg="#fdf4ff" iconColor="#7e22ce"               label="Total Managers"   value={stats.totalManagers}  sub="Across all projects" />
          </div>
          <div className="col-12 col-sm-6 col-xl-3">
            <StatCard icon={FolderOpen}   iconBg="#fff7ed" iconColor="var(--orangeColor)"    label="No Managers"      value={stats.noManagers} sub="Active projects needing assignment" />
          </div>
        </div>

        <section className="url-list-toolbar p-3 mb-3">
          <div className="url-list-actions">
            <div className="url-search-field">
              <i className="fa-solid fa-magnifying-glass"></i>
              <input
                type="search"
                className="form-control"
                placeholder="Search with Project PID, Project Name, domain..."
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
              />
            </div>

            <button
              className="btn btn-primary url-settings-btn"
              onClick={() => navigate("/project-add")}
            >
              <Plus size={16} className="me-2" />
              Create Project
            </button>
          </div>
        </section>

        <section className="url-table-card">
          <div className="table-responsive">
            <table className="table url-table table-hover align-middle mb-0">
              <colgroup>
                <col style={{ width: "11%" }} />
                <col style={{ width: "22%" }} />
                <col style={{ width: "17%" }} />
                <col style={{ width: "12%" }} />
                <col style={{ width: "13%" }} />
                <col style={{ width: "13%" }} />
              </colgroup>

              <thead>
                <tr>
                  <th className="text-center">Project ID</th>
                  <th>Project Name</th>
                  <th>URL</th>
                  <th>Project Managers</th>
                  <th>Status Action</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="6" className="text-center text-muted py-5">Loading...</td></tr>
                ) : error ? (
                  <tr><td colSpan="6" className="text-center text-danger py-5">{error}</td></tr>
                ) : paginatedProjects.length === 0 ? (
                  <tr><td colSpan="6" className="text-center text-muted py-5">No Projects found.</td></tr>
                ) : (
                  paginatedProjects.map((item, idx) => {
                    const palette = AVATAR_COLORS[idx % AVATAR_COLORS.length];
                    const isActive = (projectStatuses[item.project_pid] ?? "active") === "active";
                    return (
                      <tr
                        className="history-row"
                        key={item.project_pid ?? idx}
                        onClick={() => navigate(`/project-details/${item.project_pid}`)}
                      >
                        <td className="url-index text-center">{formatPid(item.project_pid)}</td>

                        <td>
                          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <div style={{
                              width: 32, height: 32, borderRadius: "50%", flexShrink: 0,
                              background: palette.bg, color: palette.color,
                              display: "flex", alignItems: "center", justifyContent: "center",
                              fontSize: 13, fontWeight: 800,
                            }}>
                              {(item.project_name ?? "?").charAt(0).toUpperCase()}
                            </div>
                            <span className="url-page-title">{item.project_name}</span>
                          </div>
                        </td>

                        <td>
                          <span style={{ fontSize: 12, color: "var(--primaryColor)", fontWeight: 500 }}>
                            {item.url ?? "—"}
                          </span>
                        </td>

                        <td>
                          <span className="ph-session-badge">{item.manager_count ?? 0}</span>
                        </td>

                        <td onClick={(e) => e.stopPropagation()}>
                          <button
                            onClick={(e) => toggleStatus(e, item.project_pid)}
                            style={{
                              display: "inline-flex", alignItems: "center", gap: 5,
                              fontSize: 11, fontWeight: 700,
                              padding: "4px 10px", borderRadius: 999,
                              border: "none", cursor: "pointer",
                              background: isActive ? "#f0fdf4" : "#fff1f2",
                              color: isActive ? "#166534" : "#dc2626",
                              transition: "all 0.15s",
                            }}
                            title={isActive ? "Click to suspend" : "Click to activate"}
                          >
                            {isActive ? <><CheckCircle size={11} /> Active</> : <><Ban size={11} /> Suspended</>}
                          </button>
                        </td>

                        <td onClick={(e) => e.stopPropagation()}>
                          <div className="d-flex align-items-center gap-3">
                            <button
                              className="ph-view-btn"
                              onClick={(e) => { e.stopPropagation(); navigate(`/project-details/${item.project_pid}`); }}
                            >
                              View <ChevronRight size={13} />
                            </button>
                            <Trash2
                              size={18}
                              className="text-danger cursor-pointer"
                              onClick={(e) => { e.stopPropagation(); setSelectedProject(item); setShowDeleteModal(true); }}
                            />
                            <a
                              href={`/#/dashboard?pid=${item.project_pid}&name=${encodeURIComponent(item.project_name ?? "")}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              title="Open project dashboard"
                              onClick={(e) => e.stopPropagation()}
                              style={{
                                background: "var(--primaryTint)", borderRadius: 8,
                                width: 32, height: 32, display: "flex", alignItems: "center",
                                justifyContent: "center", cursor: "pointer", color: "var(--primaryColor)",
                                flexShrink: 0, transition: "background 0.15s", textDecoration: "none",
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.background = "var(--primaryTintBorder)"}
                              onMouseLeave={(e) => e.currentTarget.style.background = "var(--primaryTint)"}
                            >
                              <ArrowUpRightFromCircle size={15} />
                            </a>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          {!loading && totalCount > 0 && (
            <div className="url-pagination-wrap">
              <p className="url-pagination-summary mb-0">
                Showing {pageStartIndex + 1}–{Math.min(pageStartIndex + itemsPerPage, totalCount)} of {totalCount}
              </p>
              <nav aria-label="URL list pagination">
                <ul className="url-pagination mb-0">
                  <li>
                    <button
                      type="button"
                      className="url-page-btn"
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage((page) => page - 1)}
                    >
                      <ChevronLeft size={16} />
                    </button>
                  </li>
                  {paginationItems.map((item) => (
                    <li key={item}>
                      {typeof item === "string" ? (
                        <span className="url-page-ellipsis">...</span>
                      ) : (
                        <button
                          type="button"
                          className={`url-page-btn ${
                            item === currentPage ? "active" : ""
                          }`}
                          onClick={() => setCurrentPage(item)}
                        >
                          {item}
                        </button>
                      )}
                    </li>
                  ))}
                  <li>
                    <button
                      type="button"
                      className="url-page-btn"
                      disabled={currentPage === totalPages}
                      onClick={() => setCurrentPage((page) => page + 1)}
                    >
                      <ChevronRight size={16} />
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          )}
        </section>
      </div>
      <DeleteConfirmModal
        show={showDeleteModal}
        type="project"
        value={selectedProject?.project_name || ""}
        onClose={() => { setShowDeleteModal(false); setSelectedProject(null); }}
        onConfirm={handleDeleteConfirm}
      />

      {/* Suspend confirmation modal */}
      {showSuspendModal && (
        <div
          style={{ position: "fixed", inset: 0, zIndex: 2000, background: "rgba(15,23,42,0.45)", display: "flex", alignItems: "center", justifyContent: "center" }}
          onClick={() => { setShowSuspendModal(false); setPendingSuspendPid(null); }}
        >
          <div
            style={{ background: "#fff", borderRadius: 14, width: "100%", maxWidth: 420, boxShadow: "0 8px 32px rgba(15,23,42,0.18)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px", borderBottom: "1px solid var(--borderColor)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 34, height: 34, borderRadius: 9, background: "#fff7ed", color: "#c2410c", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <AlertTriangle size={17} />
                </div>
                <h6 style={{ margin: 0, fontWeight: 700, fontSize: 14, color: "#0f172a" }}>Suspend Project</h6>
              </div>
              <button style={{ background: "none", border: "none", cursor: "pointer", color: "#64748b", padding: 4 }} onClick={() => { setShowSuspendModal(false); setPendingSuspendPid(null); }}>
                <X size={18} />
              </button>
            </div>

            <div style={{ padding: "20px" }}>
              <p style={{ margin: "0 0 6px", fontSize: 14, color: "#334155" }}>
                Are you sure you want to <strong>suspend</strong> this project?
              </p>
              <p style={{ margin: 0, fontSize: 12, color: "#94a3b8" }}>
                Suspending will disable all API access for this project. You can reactivate it at any time.
              </p>
            </div>

            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", padding: "0 20px 20px" }}>
              <button
                className="btn btn-light"
                style={{ fontSize: 13, borderRadius: 8 }}
                onClick={() => { setShowSuspendModal(false); setPendingSuspendPid(null); }}
              >
                Cancel
              </button>
              <button
                className="btn btn-warning d-flex align-items-center gap-2"
                style={{ fontSize: 13, borderRadius: 8, fontWeight: 600 }}
                onClick={confirmSuspend}
              >
                <Ban size={14} /> Yes, Suspend
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
};

export default ProjectList;
